// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Platform, StyleSheet } from 'react-native';
import appButton from '@/theme/button';
import appColors, { alpha } from '@/theme/colors';
import { panelTextShadow } from '@/theme/textShadow';
import appFonts from '@/theme/typography';

const blockShadow = Platform.select({
  web: { boxShadow: '0px 2px 8px rgba(0,0,0,0.14)' },
  default: {
    shadowColor: appColors.neutral.black,
    shadowOpacity: 0.14,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 2 },
  },
});

export const styles = StyleSheet.create({
  scroll: {
    backgroundColor: appColors.background.app,
  },
  compactScroll: {
    flex: 1,
  },
  compactScrollContent: {
    flexGrow: 1,
    paddingBottom: 180,
  },
  scrollContent: {
    flexGrow: 1,
  },
  compactList: {
    gap: 10,
    marginTop: 8,
    backgroundColor: 'transparent',
  },
  compactRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 12,
    backgroundColor: appColors.surface.base,
    borderWidth: 1,
    borderColor: appColors.border.default,
  },
  compactInfoColumn: {
    flex: 1,
    marginRight: 10,
    backgroundColor: 'transparent',
  },
  compactNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap',
    backgroundColor: 'transparent',
  },
  compactNameLine: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    flexShrink: 1,
    backgroundColor: 'transparent',
  },
  compactNameBlock: {
    width: 200,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: 'transparent',
  },
  compactAvatarWrap: {
    width: 28,
    height: 28,
    borderRadius: 10,
    overflow: 'hidden',
    borderWidth: 0,
    borderColor: 'transparent',
    backgroundColor: 'transparent',
  },
  compactAvatar: {
    width: '100%',
    height: '100%',
  },
  // For an image inside a slot the parent already sized — see CyclingSlot's `style` prop.
  slotFill: {
    width: '100%',
    height: '100%',
  },
  compactName: {
    color: appColors.text.heading,
    fontSize: 16,
    fontWeight: '700',
    flexShrink: 1,
  },
  roundPlacementBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    backgroundColor: 'transparent',
  },
  roundPlacementMedal: {
    fontSize: 13,
  },
  compactLeaderTrophy: {
    color: appColors.accent.lime,
  },
  compactHandicapRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: 'transparent',
  },
  compactHandicapText: {
    color: appColors.text.secondary,
    fontSize: 14,
    fontWeight: '600',
  },
  compactScoreButton: {
    width: 118,
    height: 52,
    borderRadius: 0,
    backgroundColor: 'transparent',
    borderWidth: 0,
    borderColor: 'transparent',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    shadowColor: appColors.neutral.slate,
    shadowOpacity: 0,
    shadowRadius: 0,
    shadowOffset: { width: 0, height: 0 },
    elevation: 0,
  },
  compactScoreButtonImage: {
    width: '100%',
    height: '100%',
  },
  // The same 118x52 slot with no plank behind it: a card with the skin's corner and rule.
  compactScoreButtonDrawn: {
    borderRadius: appButton.radius,
    backgroundColor: appColors.surface.card,
    borderWidth: 1,
    borderColor: appColors.border.strong,
  },
  compactScoreActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: 'transparent',
  },
  compactScoreMysteryIcon: {
    width: 20,
    height: 20,
  },
  // Tour-long Freeze standing on this hole, from the tour's second round on:
  // the points banked (or still pending) left of a snowflake that is filled
  // once the hole is locked and outlined while it is still in play.
  compactScoreFreezeBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    backgroundColor: 'transparent',
  },
  compactScoreFreezeBadgePoints: {
    color: appColors.accent.freeze,
    fontSize: 14,
    fontWeight: '800',
    minWidth: 12,
    textAlign: 'right',
  },
  // Best Eclectic points already banked on this hole in an earlier tour round,
  // shown as "(2)" left of the score button — the number to beat.
  compactScoreEclecticBadge: {
    color: appColors.text.secondary,
    fontSize: 14,
    fontWeight: '800',
    minWidth: 22,
    textAlign: 'center',
  },
  compactScoreButtonText: {
    position: 'absolute',
    width: '100%',
    textAlign: 'center',
    lineHeight: 52,
    color: appColors.accent.lime,
    fontFamily: appFonts.brand,
    fontWeight: '800',
    fontSize: 18,
    textShadowColor: alpha(appColors.neutral.black, 0.45),
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 3,
  },
  compactScoreButtonTextPlaceholder: {
    color: appColors.accent.lime,
  },
  // Ink on a card rather than paint on wood: no drop shadow to lift the number off a plank,
  // and a heading colour, because lime on cream cannot be read.
  //
  // Written out in full and used INSTEAD of compactScoreButtonText, not on top of it:
  // react-native-web composes textShadowColor/Offset/Radius into one `text-shadow` per style
  // object, so a later object naming those keys does not override an earlier object's composed
  // shorthand — the plank's shadow survives onto the card. The same holds for the shadow* keys
  // and `box-shadow`. Plain keys like `color` do override; shorthand-composed ones do not.
  compactScoreButtonTextDrawn: {
    position: 'absolute',
    width: '100%',
    textAlign: 'center',
    lineHeight: 52,
    color: appColors.text.heading,
    fontFamily: appFonts.brand,
    fontWeight: '800',
    fontSize: 18,
  },
  compactScoreButtonTextPlaceholderDrawn: {
    color: appColors.text.placeholder,
  },
  compactScoreHazardBadges: {
    position: 'absolute',
    right: 6,
    top: 6,
    flexDirection: 'row',
    gap: 4,
    alignItems: 'center',
    backgroundColor: 'transparent',
  },
  compactScoreHazardBadge: {
    width: 20,
    height: 20,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // One animal at a time on the right of the 118x52 plank, as large as the wooden frame allows.
  compactScoreHazardCritter: {
    position: 'absolute',
    right: 6,
    top: 6,
    width: 40,
    height: 40,
  },
  compactScoreHazardBadgeWater: {
    backgroundColor: appColors.background.interactive,
  },
  compactScoreHazardBadgeSand: {
    backgroundColor: appColors.status.warningBorderMuted,
  },
  compactScoreHazardBadgeThreeputt: {
    backgroundColor: appColors.text.primary,
  },
  compactScoreHazardBadgeOther: {
    backgroundColor: appColors.accent.lime,
  },
  compactScoreHazardBadgeOut: {
    // White stakes mark out of bounds; the badge borrows that, dark enough for cream text.
    backgroundColor: appColors.neutral.greyDark,
  },
  compactScoreHazardBadgeTextBase: {
    fontSize: 9,
    fontWeight: '800',
    lineHeight: 9,
  },
  compactScoreHazardBadgeTextLight: {
    color: appColors.text.primary,
  },
  compactScoreHazardBadgeTextDark: {
    // Dark text for badges with light backgrounds (three-putt beige, penalty lime); the cream
    // TextLight color was invisible on the near-white three-putt badge.
    color: appColors.text.inverse,
  },
  compactStablefordText: {
    color: appColors.accent.mint,
    fontWeight: '700',
    fontSize: 18,
    minWidth: 16,
    textAlign: 'center',
  },
  scorePopupGrid: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    marginTop: 6,
    marginLeft: 0,
    paddingBottom: 18,
    width: 338,
      left:10,
    overflow: 'visible'
  },
  // Drawn skins have no rail to tuck the column behind, so it takes its own full width and the
  // buttons stop hanging 38px past it. The width comes from the children rather than a number:
  // a round with no hazard game and no enhanced input leaves this column empty, and a fixed
  // width would then park a dead 126px gutter beside the scores. The artwork layout hid the
  // same emptiness by pulling the score column back over it.
  scorePopupHazardColumnDrawn: {
    width: 'auto',
    alignItems: 'center',
    gap: 10,
    paddingTop: 8,
    backgroundColor: 'transparent',
  },
  scorePopupHazardButtonDrawn: {
    marginLeft: 0,
  },
  scorePopupHazardColumn: {
    width: 74,
    alignItems: 'center',
    gap: 10,
    paddingTop: 34,
    marginLeft: 0,
    backgroundColor: 'transparent',
  },
  scorePopupHazardButton: {
    width: 126,
    minHeight: 78,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    marginLeft: 38,
    backgroundColor: 'transparent',
  },
  scorePopupHazardLabel: {
    color: appColors.text.warm,
    fontSize: 10,
    lineHeight: 11,
    textTransform: 'uppercase',
    textAlign: 'center',
  },
  scorePopupHazardControlRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    backgroundColor: 'transparent',
  },
  scorePopupHazardAdjustButton: {
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.status.warningBorderMuted,
    backgroundColor: alpha(appColors.surface.brown, 0.75),
  },
  scorePopupHazardAdjustText: {
    color: appColors.text.warm,
    fontSize: 18,
    lineHeight: 18,
    fontWeight: '800',
    textAlign: 'center',
  },
  scorePopupHazardIconWrap: {
    width: 52,
    height: 52,
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
  },
  // Separates the game inputs above from the personal shot-detail inputs below.
  scorePopupEnhancedDivider: {
    width: 84,
    height: 1,
    marginVertical: 2,
    backgroundColor: alpha(appColors.text.dim, 0.45),
  },
  // Slate rather than the wooden plaques above: these three inputs are personal and score
  // nothing, so they should not look like the game tiles. Slate also stays out of the way of
  // the accent green, which this column already spends on "selected".
  scorePopupEnhancedTile: {
    borderRadius: 6,
    borderWidth: 2,
    borderColor: appColors.text.dim,
    backgroundColor: appColors.text.onLight,
    // Inner bevel, so the tile still reads as a carved plaque next to the wood.
    ...Platform.select({
      web: { boxShadow: 'inset 0 0 0 1px #24322c' },
      default: {
        shadowColor: appColors.surface.muted,
        shadowOpacity: 0.9,
        shadowRadius: 1,
        shadowOffset: { width: 0, height: 1 },
      },
    }),
  },
  // The drawn skins' tile, used INSTEAD of scorePopupEnhancedTile, for the shadow reason noted
  // at compactScoreButtonTextDrawn. The slate is text.onLight, which a paper skin makes the same
  // dark ink as the text.primary drawn on it: the putts and GIR values vanished at 1:1. A
  // recessed paper tone keeps these three apart from the card-backed game tiles and still takes
  // text.primary. styles.test.ts holds every skin's tile to its ink.
  scorePopupEnhancedTileDrawn: {
    borderRadius: 6,
    borderWidth: 2,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.surface.muted,
  },
  /** Three side-by-side fairway buttons, sized so left/hit/right fit the hazard column's width. */
  scorePopupFairwayButton: {
    width: 38,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 1,
  },
  scorePopupFairwayButtonText: {
    color: appColors.text.primary,
    fontSize: 9,
    fontWeight: '800',
    textAlign: 'center',
  },
  scorePopupEnhancedTileSelected: {
    borderColor: appColors.accent.primary,
  },
  /** Nothing entered yet — dimmed, the same signal chip in uses for its untouched ball. */
  scorePopupEnhancedTileUnset: {
    opacity: 0.6,
  },
  scorePopupEnhancedTileText: {
    color: appColors.text.primary,
    fontSize: 13,
    fontWeight: '800',
    textAlign: 'center',
    textShadowColor: alpha(appColors.neutral.black, 0.75),
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  // Ink on paper rather than paint on slate: no drop shadow. Written out in full and used
  // instead of scorePopupEnhancedTileText, like compactScoreButtonTextDrawn.
  scorePopupEnhancedTileTextDrawn: {
    color: appColors.text.primary,
    fontSize: 13,
    fontWeight: '800',
    textAlign: 'center',
  },
  // The putts count on the drawn tile, in place of scorePopupHazardCount. Not
  // scorePopupHazardCountDrawn: that is a corner badge beside a glyph, and the putts tile has
  // no glyph, so the number is its whole face.
  scorePopupEnhancedCountDrawn: {
    minWidth: 18,
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    textAlign: 'center',
    color: appColors.text.primary,
    fontSize: 24,
    lineHeight: 52,
    fontWeight: '800',
  },
  // On/off treatment for a square hazard tile — chip in's selected look on the
  // painted tile rather than the round ball, so three putts keeps its artwork.
  scorePopupHazardToggle: {
    borderRadius: 10,
    borderWidth: 2,
    borderColor: 'transparent',
    opacity: 0.55,
  },
  scorePopupHazardToggleSelected: {
    borderColor: appColors.border.accent,
    backgroundColor: alpha(appColors.accent.primary, 0.18),
    opacity: 1,
  },
  scorePopupChipInToggle: {
    width: 56,
    height: 56,
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 28,
    borderWidth: 2,
    borderColor: 'transparent',
    backgroundColor: 'transparent',
    opacity: 0.55,
  },
  scorePopupChipInToggleSelected: {
    borderColor: appColors.border.accent,
    backgroundColor: alpha(appColors.accent.primary, 0.18),
    opacity: 1,
  },
  scorePopupChipInCheck: {
    position: 'absolute',
    right: 2,
    bottom: 2,
    color: appColors.accent.primary,
    fontSize: 18,
    fontWeight: '800',
    // Over the painted animals in the dark skins, but on a drawn tile in the light ones.
    ...panelTextShadow(0.75, 1, 2),
  },
  scorePopupHazardIcon: {
    width: 52,
    height: 52,
    position: 'relative',
  },
  // The tile the glyph sits on where there is no painted animal to carry the shape itself.
  scorePopupHazardTileDrawn: {
    borderRadius: appButton.radius,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.surface.card,
  },
  // A border the toggle can dim rather than the transparent one the artwork wants.
  scorePopupHazardToggleDrawn: {
    borderColor: appColors.border.strong,
    opacity: 0.7,
  },
  scorePopupChipInToggleDrawn: {
    borderColor: appColors.border.strong,
    backgroundColor: appColors.surface.card,
    opacity: 0.7,
  },
  // Centring the count over a glyph would bury it; on a drawn tile it becomes a corner badge,
  // filled once the hazard has actually been picked up.
  scorePopupHazardCountDrawn: {
    position: 'absolute',
    right: -4,
    bottom: -4,
    width: 22,
    height: 22,
    borderRadius: 11,
    overflow: 'hidden',
    textAlign: 'center',
    fontSize: 12,
    lineHeight: 22,
    fontWeight: '800',
    // accent.primary is a light fill in every skin, so the count takes the skin's inverse ink
    // rather than white: text.onStrong on it reads at 1.7:1.
    color: appColors.text.inverse,
    backgroundColor: appColors.accent.primary,
  },
  scorePopupHazardCountDrawnEmpty: {
    color: appColors.text.primary,
    backgroundColor: appColors.surface.muted,
  },
  scorePopupHazardCount: {
    minWidth: 18,
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    textAlign: 'center',
    color: appColors.text.primary,
    fontSize: 24,
    lineHeight: 52,
    fontWeight: '800',
    textShadowColor: alpha(appColors.neutral.black, 0.75),
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  scorePopupMysteryButton: {
    width: 74,
    minHeight: 72,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    paddingVertical: 4,
    borderWidth: 2,
    borderColor: 'transparent',
    borderRadius: 8,
  },
  scorePopupMysteryButtonSelected: {
    borderColor: appColors.text.warm,
    backgroundColor: alpha(appColors.text.warm, 0.18),
  },
  scorePopupMysteryIcon: {
    width: 34,
    height: 34,
  },
  scorePopupMysteryText: {
    color: appColors.text.warm,
    fontSize: 10,
    lineHeight: 12,
    textAlign: 'center',
    width: '100%',
    paddingHorizontal: 2,
  },
  scorePopupMysterySetupButton: {
    width: 126,
    minHeight: 78,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    marginLeft: 38,
    backgroundColor: 'transparent',
  },
  scorePopupLeftColumn: {
    width: 200,
    alignItems: 'flex-start',
    gap: 0,
    backgroundColor: 'transparent',
    position: 'relative',
    zIndex: 300,
    elevation: 300,
    paddingTop: 30,
    overflow: 'visible'
  },
  // Touch-transparent carriers for the plate and rail artwork, at the layers the artwork itself
  // held: the plate over the peg marks, the rail under them.
  scorePopupPlateLayer: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 400,
    elevation: 400,
  },
  scorePopupRailLayer: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 320,
    elevation: 320,
  },
  scorePopupTopPlateImage: {
    width: 430,
    height: 85,
    marginLeft: -45,
    position: 'absolute',
    top: -4,
    left: 0,
    zIndex: 400,
    elevation: 400,
  },
  scorePopupTopPlateText: {
    position: 'absolute',
    top: 18,
    left: 124,
    zIndex: 401,
    elevation: 401,
    color: appColors.text.warm,
    fontSize: 32,
    fontFamily: appFonts.brand,
    fontWeight: '800',
    textShadowColor: alpha(appColors.neutral.black, 0.45),
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 3,
  },
  scorePopupRailImage: {
    width: 60,
    height: 500,
    position: 'absolute',
    top: 44,
    left: 59,
    backgroundColor: 'transparent',
    borderRadius: 0,
    zIndex: 320,
    elevation: 320,
  },
  scorePopupRailMarksColumn: {
    position: 'absolute',
    top: 102,
    left: 22,
    zIndex: 360,
    elevation: 360,
    gap: 0,
  },
  scorePopupRailMarkIcon: {
    width: 36,
    height: 36,
    left: 48,
    backgroundColor: 'transparent',
    borderWidth: 0,
    zIndex: 340,
    elevation: 340,
    marginTop: -16,
  },
  scorePopupRailMarkSlot: {
    width: 54,
    height: 59,
    backgroundColor: 'transparent',
    borderWidth: 0,
    position: 'relative',
  },
  scorePopupRailParRing: {
    width: 32,
    height: 32,
    position: 'absolute',
    top: -15,
    left: 50,
    backgroundColor: 'transparent',
    borderWidth: 2,
    borderColor: appColors.status.warning,
    borderRadius: 999,
    zIndex: 530,
    elevation: 350,
  },
  scorePopupRailPegIcon: {
    width: 80,
    height: 80,
    position: 'absolute',
    top: -32,
    left: 27,
    zIndex: 540,
    elevation: 360,
  },
  scorePopupRailMarkIconFirst: {
    marginTop: 0,
  },
  scorePopupRows: {
    flex: 0,
    width: 124,
    marginLeft: -96,
    gap: 10,
    marginTop: 80,
    backgroundColor: 'transparent',
    position: 'relative',
    zIndex: 10,
    elevation: 10,
  },
  // With the peg rail folded into the buttons the column no longer reaches back under it, so
  // it drops the artwork layout's negative margin and 80px drop. Used instead of
  // scorePopupRows: that one's `flex: 0` becomes `flex: 0 1 0%` on react-native-web, and a
  // zero basis beats `width` — the box would measure 0 and only the buttons, which carry their
  // own width, would show. The artwork layout was built around exactly that.
  scorePopupRowsDrawn: {
    width: 190,
    gap: 8,
    backgroundColor: 'transparent',
    position: 'relative',
    zIndex: 10,
    elevation: 10,
  },
  // What the carved "Par 4" plate becomes: a plain chip at the head of the column.
  scorePopupParChipDrawn: {
    alignSelf: 'stretch',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 6,
    marginBottom: 2,
    borderRadius: appButton.radius,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.surface.elevated,
  },
  scorePopupParChipTextDrawn: {
    color: appColors.text.heading,
    fontFamily: appFonts.brand,
    fontSize: 20,
    fontWeight: '700',
  },
  scorePopupButton: {
    alignItems: 'center',
    justifyContent: 'center',
    width: 124,
    height: 49,
    marginLeft: 0,
    overflow: 'hidden',
  },
  scorePopupButtonPar: {
    opacity: 0.98,
  },
  scorePopupButtonSelected: {
    borderWidth: 2,
    borderColor: appColors.accent.goldDeep,
    borderRadius: 6,
    shadowColor: appColors.accent.goldDeep,
    shadowOpacity: 0.5,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 0 },
  },
  // The rail's eight holes are now a marker inside each button: hollow, ringed on par, filled
  // on the score that was entered. One control where there were two, same three states. Used
  // instead of scorePopupButton rather than over it — see compactScoreButtonTextDrawn for why
  // an override cannot clear the artwork's shadow.
  scorePopupButtonDrawn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: 190,
    height: 52,
    paddingHorizontal: 14,
    gap: 12,
    borderRadius: appButton.radius,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.surface.card,
    overflow: 'visible',
  },
  scorePopupButtonParDrawn: {
    borderColor: appColors.accent.primary,
    backgroundColor: appColors.accent.subtleBg,
  },
  scorePopupButtonSelectedDrawn: {
    borderWidth: 1,
    borderColor: appColors.accent.primary,
    backgroundColor: appColors.accent.primary,
    borderRadius: appButton.radius,
    shadowOpacity: 0,
    shadowRadius: 0,
  },
  scorePopupButtonMarkerDrawn: {
    width: 16,
    height: 16,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: appColors.border.strong,
    backgroundColor: 'transparent',
  },
  scorePopupButtonMarkerParDrawn: {
    borderColor: appColors.accent.primary,
  },
  scorePopupButtonMarkerSelectedDrawn: {
    borderColor: appColors.text.onStrong,
    backgroundColor: appColors.text.onStrong,
  },
  scorePopupButtonTextDrawn: {
    flex: 1,
    textAlign: 'left',
    fontSize: 26,
    lineHeight: 34,
    fontFamily: appFonts.brand,
    fontWeight: '800',
    color: appColors.text.heading,
  },
  scorePopupButtonTextSelectedDrawn: {
    color: appColors.text.onStrong,
  },
  scorePopupButtonImage: {
    width: 124,
    height: 49,
    position: 'absolute',
    top: 0,
    left: 0,
    pointerEvents: 'none',
  },
  scorePopupButtonText: {
    color: appColors.text.warm,
    fontSize: 30,
    fontFamily: appFonts.brand,
    fontWeight: '800',
    position: 'absolute',
    top: 0,
    left: -2,
    width: 124,
    textAlign: 'center',
    lineHeight: 44,
    textShadowColor: alpha(appColors.neutral.black, 0.45),
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 3,
    pointerEvents: 'none',
  },
  scorePopupButtonTextPar: {
    color: appColors.text.warm,
  },
  // Android stacks siblings by elevation before zIndex, and the new architecture flattens the
  // background-less scorePopupGrid, so the rail (elevation 300) and the score column (10) become
  // this overlay's siblings. Without an elevation above theirs the 8+ picker drew under them and
  // every tap on it landed on the score buttons instead.
  scorePopupEightPlusOverlay: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 30,
    elevation: 500,
    justifyContent: 'center',
    alignItems: 'center',
  },
  scorePopupEightPlusBackdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: alpha(appColors.surface.brownDeep, 0.6),
  },
  scorePopupEightPlusCard: {
    minWidth: 140,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.status.warningBorderMuted,
    backgroundColor: appColors.surface.brown,
    paddingVertical: 8,
    paddingHorizontal: 8,
    gap: 6,
  },
  scorePopupEightPlusOption: {
    borderRadius: 8,
    borderWidth: 1,
    borderColor: appColors.status.warning,
    backgroundColor: appColors.surface.brownLight,
    paddingVertical: 8,
    paddingHorizontal: 12,
    alignItems: 'center',
  },
  scorePopupEightPlusOptionText: {
    color: appColors.text.warm,
    fontSize: 16,
    fontWeight: '700',
  },
  // Elevation for the same reason as the 8+ picker: the close button beside it has elevation 4.
  scoreChangeConfirmOverlay: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 40,
    elevation: 500,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  scoreChangeConfirmBackdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: alpha(appColors.surface.brownDeep, 0.6),
    borderRadius: 16,
  },
  scoreChangeConfirmCard: {
    maxWidth: 320,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.status.warningBorderMuted,
    backgroundColor: appColors.surface.brown,
    paddingVertical: 16,
    paddingHorizontal: 16,
    gap: 12,
  },
  scoreChangeConfirmActions: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 12,
  },
  scoreChangeConfirmButton: {
    flexGrow: 1,
    flexBasis: 0,
  },
  pageBackground: {
    flex: 1,
    width: '100%',
    backgroundColor: appColors.background.app,
    position: 'relative',
  },
  container: {
    padding: 12,
    gap: 8,
    justifyContent: 'center',
    backgroundColor: 'transparent',
  },
  containerCompact: {
    justifyContent: 'flex-start',
  },
  compactRoot: {
    flex: 1,
    position: 'relative',
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    justifyContent: 'space-between',
    backgroundColor: 'transparent',
  },
  holeTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
    backgroundColor: 'transparent',
  },
  holeTitleSign: {
    minWidth: 96,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 22,
    paddingVertical: 10,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: appColors.status.successBg,
    backgroundColor: appColors.border.strong,
    ...Platform.select({
      web: { boxShadow: 'inset 0px 10px 18px rgba(161,212,147,0.2), 0px 3px 10px rgba(0,0,0,0.28)' },
      default: {
        shadowColor: appColors.neutral.black,
        shadowOpacity: 0.28,
        shadowRadius: 8,
        shadowOffset: { width: 0, height: 2 },
      },
    }),
  },
  holeTitleNumber: {
    fontSize: 34,
    fontWeight: '800',
    letterSpacing: 0.8,
    color: appColors.text.primary,
    textAlign: 'center',
    ...Platform.select({
      web: { fontFamily: appFonts.webSerif },
      default: { fontFamily: appFonts.systemSerif },
    }),
    backgroundColor: 'transparent',
  },
  mysteryPrizeIcon: {
    width: 23,
    height: 23,
  },
  mysteryWinnerIcon: {
    width: 16,
    height: 16,
  },
  mysteryPrizeText: {
    color: appColors.text.secondary,
    fontSize: 13,
    textAlign: 'center',
  },
  mysteryPrizeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    backgroundColor: 'transparent',
  },
  skinsCarryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.accent.goldBorder,
    backgroundColor: appColors.accent.goldSoft,
    paddingVertical: 5,
    paddingHorizontal: 10,
  },
  skinsCarryText: {
    color: appColors.accent.gold,
    fontSize: 13,
    fontWeight: '700',
    textAlign: 'center',
  },
  missingScoreBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.status.danger,
    backgroundColor: alpha(appColors.status.danger, 0.14),
    paddingVertical: 6,
    paddingHorizontal: 10,
    marginBottom: 6,
  },
  missingScoreText: {
    flex: 1,
    color: appColors.status.danger,
    fontSize: 13,
    fontWeight: '700',
  },
  missingScoreDismiss: {
    color: appColors.status.danger,
    fontSize: 16,
    fontWeight: '700',
    paddingHorizontal: 4,
  },
  shotTrackerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    borderRadius: 10,
    paddingVertical: 3,
    paddingHorizontal: 10,
  },
  shotTrackerText: {
    color: appColors.accent.primary,
    fontSize: 12,
    fontWeight: '600',
    textAlign: 'center',
  },
  mysteryPrizeSetupButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 6,
    paddingHorizontal: 14,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panelAlt,
  },
  mysteryPrizeSetupButtonText: {
    color: appColors.text.primary,
    fontSize: 13,
    fontWeight: '600',
  },
  meta: {
    textAlign: 'center',
    color: appColors.text.muted,
  },
  holeInfoBlock: {
    alignItems: 'center',
    gap: 4,
    padding: 10,
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: 'transparent',
  },
  holeInfoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: 'transparent',
  },
  holeInfoBadge: {
    paddingVertical: 8,
    paddingHorizontal: 18,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.default,
    backgroundColor: appColors.surface.alt,
    color: appColors.text.secondary,
    fontWeight: '700',
    fontSize: 13,
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  holeInfoName: {
    color: appColors.text.heading,
    fontWeight: '700',
    textAlign: 'center',
  },
  holeInfoDescription: {
    color: appColors.text.secondary,
    textAlign: 'center',
    lineHeight: 18,
  },
  detailsToggle: {
    marginTop: 4,
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.default,
    backgroundColor: appColors.surface.alt,
  },
  detailsToggleText: {
    color: appColors.text.secondary,
    fontWeight: '600',
  },
  metaRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 12,
  },
  subtitle: {
    textAlign: 'center',
    fontSize: 16,
    color: appColors.text.heading,
  },
  error: {
    textAlign: 'center',
    color: appColors.status.errorText,
  },
  scoreboard: {
    padding: 8,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.default,
    backgroundColor: appColors.surface.base,
    gap: 8,
    flexDirection: 'column',
    ...blockShadow,
  },
  gamesBallTabs: {
    flexDirection: 'row',
    gap: 6,
    paddingBottom: 8,
    backgroundColor: 'transparent',
  },
  gamesBallTab: {
    paddingHorizontal: 12,
    paddingVertical: 5,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: alpha(appColors.accent.glow, 0.45),
    backgroundColor: alpha(appColors.background.elevated, 0.86),
  },
  gamesBallTabActive: {
    borderColor: appColors.accent.lime,
    backgroundColor: alpha(appColors.status.successBg, 0.55),
  },
  gamesBallTabText: {
    color: appColors.text.faint,
    fontSize: 12,
    fontWeight: '700',
  },
  gamesBallTabTextActive: {
    color: appColors.accent.lime,
  },
  gameResultsFrame: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.border.default,
    backgroundColor: appColors.surface.base,
    padding: 6,
  },
  scoreboardTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: appColors.text.heading,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  scoreboardColumnsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: 2,
    backgroundColor: 'transparent',
  },
  scoreboardNameHeaderSpacer: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  scoreboardNameHeader: {
    flex: 1,
    fontSize: 16,
    fontWeight: '700',
    color: appColors.text.heading,
    backgroundColor: 'transparent',
  },
  scoreboardScoreColumnHeader: {
    width: 54,
    textAlign: 'right',
    fontSize: 11,
    fontWeight: '700',
    color: appColors.text.secondary,
    textTransform: 'uppercase',
  },
  scoreboardEmpty: {
    color: appColors.text.secondary,
    fontSize: 13,
  },
  scoreboardBlindFinish: {
    color: appColors.text.secondary,
    fontSize: 12,
    fontStyle: 'italic',
    marginBottom: 6,
  },
  scoreboardRow: {
    position: 'relative',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 4,
    backgroundColor: 'transparent',
  },
  scoreboardNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexShrink: 1,
    backgroundColor: 'transparent',
  },
  scoreboardName: {
    fontWeight: '600',
    color: appColors.text.heading,
  },
  scoreboardValue: {
    width: 54,
    textAlign: 'right',
    fontWeight: '700',
    color: appColors.accent.primary,
    backgroundColor: 'transparent',
  },
  scoreboardScores: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    minWidth: 116,
    backgroundColor: 'transparent',
  },
  scoreboardValueDetails: {
    textDecorationLine: 'underline',
  },
  gameResultsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    // Tight on purpose: the gutters were eating the width the player names need, and a name
    // truncated to "Sp. Plan..." tells you less than the couple of pixels of spacing were worth.
    gap: 2,
    width: '100%',
    paddingVertical: 2,
    backgroundColor: 'transparent',
  },
  gameResultsGroup: {
    width: '100%',
    backgroundColor: 'transparent',
  },
  gameResultsGroupHeader: {
    fontSize: 10,
    fontWeight: '700',
    color: appColors.text.hint,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    paddingTop: 4,
    paddingBottom: 1,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: appColors.accent.overlay,
    justifyContent: 'center',
    padding: 14,
  },
  modalCard: {
    backgroundColor: appColors.surface.base,
    borderRadius: 16,
    padding: 20,
    borderWidth: 1,
    borderColor: appColors.border.default,
    gap: 12,
  },
  mysteryModalFrame: {
    backgroundColor: appColors.surface.elevated,
    borderRadius: 18,
    borderWidth: 2,
    borderColor: appColors.border.strong,
    padding: 6,
    shadowColor: appColors.neutral.black,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.4,
    shadowRadius: 12,
    elevation: 10,
  },
  mysteryModalInner: {
    backgroundColor: appColors.surface.base,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    padding: 18,
    gap: 12,
  },
  scoreModalCard: {
    backgroundColor: appColors.text.dim,
    borderColor: appColors.surface.muted,
    borderWidth: 2,
    borderRadius: 12,
    width: 560,
    maxWidth: '100%',
    paddingHorizontal: 4,
    paddingVertical: 2,
    marginBottom: -10,
  },
  scoreModalBackdrop: {
    justifyContent: 'flex-end',
  },
  scoreModalBackdropTop: {
    justifyContent: 'flex-start',
    paddingTop: 58,
  },
  scorePopupScroll: {
    width: '100%',
  },
  scorePopupScrollContent: {
    paddingBottom: 8,
  },
  scorePopupTitle: {
    color: appColors.text.primary,
    fontSize: 20,
    marginTop: 8,
    ...panelTextShadow(0.45, 1, 1),
  },
  scorePopupSubtitle: {
    color: appColors.text.primary,
    fontSize: 13,
    fontWeight: '700',
    textAlign: 'center',
    marginTop: 4,
    marginBottom: 2,
  },
  scorePopupCloseButton: {
    position: 'absolute',
    top: 8,
    right: 8,
    width: 36,
    height: 36,
    minWidth: 36,
    paddingHorizontal: 0,
    paddingVertical: 0,
    marginTop: 0,
    zIndex: 4,
    elevation: 4,
  },
  scorePopupCloseButtonText: {
    fontSize: 18,
    lineHeight: 18,
    fontWeight: '800',
    letterSpacing: 0,
    textTransform: 'none',
  },
  modalTitle: {
    color: appColors.text.heading,
    fontSize: 18,
    fontWeight: '700',
    textAlign: 'center',
  },
  modalSubtitle: {
    color: appColors.text.secondary,
    fontSize: 13,
    textAlign: 'center',
  },
  infoIconSmall: {
    width: 14,
    height: 14,
    borderRadius: 7,
    borderWidth: 1,
    borderColor: appColors.accent.teal,
    alignItems: 'center',
    justifyContent: 'center',
  },
  infoIconSmallText: {
    color: appColors.text.muted,
    fontSize: 10,
    fontWeight: '700',
  },
  modalRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 4,
    borderBottomWidth: 1,
    borderBottomColor: appColors.border.default,
    gap: 10,
  },
  modalLabel: {
    color: appColors.text.heading,
  },
  modalLabelGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  modalGameIcon: {
    width: 40,
    height: 40,
    borderRadius: 4,
  },
  // Keeps the header's game label above the rows' game labels, where the icon sits.
  modalGameIconSpacer: {
    width: 40,
    height: 1,
  },
  // Fixed width whether or not the game ranks anyone, so every game name starts at the same x.
  // Sized for the "POS" header rather than the digit under it — at the number's own width the
  // header wrapped to two lines.
  modalGamePosition: {
    width: 40,
    textAlign: 'center',
    color: appColors.text.secondary,
    fontWeight: '700',
  },
  playerBreakdownTotalRow: {
    borderTopWidth: 1,
    borderTopColor: alpha(appColors.status.successBg, 0.65),
    paddingTop: 8,
    marginTop: 4,
  },
  // Matches the scroll content's indicator gutter so the total lines up with the values above it.
  playerBreakdownTotalGutter: {
    paddingRight: 12,
  },
  playerBreakdownTotalLabel: {
    flex: 1,
    color: appColors.text.heading,
    fontSize: 14,
    fontWeight: '800',
    textTransform: 'uppercase',
  },
  playerBreakdownTotalValue: {
    fontWeight: '800',
  },
  playerBreakdownHeaderRow: {
    borderBottomWidth: 1,
    borderBottomColor: alpha(appColors.status.successBg, 0.45),
    paddingBottom: 6,
    marginBottom: 4,
  },
  playerBreakdownHeaderText: {
    color: appColors.text.secondary,
    fontSize: 11,
    fontWeight: '800',
    textTransform: 'uppercase',
  },
  modalValue: {
    color: appColors.accent.primary,
    fontWeight: '700',
  },
  modalEmpty: {
    color: appColors.text.muted,
  },
  modalCloseButton: {
    marginTop: 8,
    alignSelf: 'flex-end',
    width: 30,
      height: 30,
  },
  playerBreakdownOkButton: {
    width: 80,
  },
  playerBreakdownCard: {
    maxHeight: '94%',
    paddingVertical: 12,
    gap: 8,
  },
  playerBreakdownScroll: {
    flexGrow: 0,
    flexShrink: 1,
  },
  playerBreakdownScrollContent: {
    gap: 8,
    paddingBottom: 4,
    // The vertical scroll indicator floats above the content rather than taking layout space, and
    // it sat right on top of the merit values. This is the gutter it lives in.
    paddingRight: 12,
  },
  modalCloseButtonBlue: {
    borderRadius: 8,
    borderWidth: 1,
    borderColor: appColors.border.default,
    backgroundColor: appColors.surface.alt,
  },
  modalBackButton: {
    alignSelf: 'flex-start',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.default,
    backgroundColor: appColors.surface.alt,
  },
  modalBackText: {
    color: appColors.text.heading,
    fontSize: 12,
    fontWeight: '700',
  },
  modalCloseText: {
    color: appColors.text.heading,
    fontWeight: '700',
    fontSize: 20,
    lineHeight: 20,
  },
  leaderboardChangeModalCard: {
    gap: 10,
    marginTop: 38,
    paddingTop: 30,
    borderWidth: 1,
    borderColor: appColors.accent.goldBorder,
    overflow: 'visible',
    shadowColor: appColors.accent.gold,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.45,
    shadowRadius: 26,
    elevation: 14,
  },
  // zIndex matches what elevation already does on Android: the badge over the title it overlaps.
  leaderboardChangeBadge: {
    position: 'absolute',
    zIndex: 1,
    top: -38,
    alignSelf: 'center',
    width: 76,
    height: 76,
    borderRadius: 38,
    backgroundColor: appColors.accent.gold,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 3,
    borderColor: appColors.surface.base,
    shadowColor: appColors.accent.gold,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.8,
    shadowRadius: 18,
    elevation: 16,
  },
  leaderboardChangeBadgeIcon: {
    fontSize: 38,
  },
  leaderboardChangeTitle: {
    color: appColors.text.heading,
    fontSize: 22,
    fontWeight: '800',
    letterSpacing: 0.3,
    textAlign: 'center',
  },
  leaderboardChangeTitleSpark: {
    color: appColors.accent.gold,
  },
  leaderboardChangeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: appColors.surface.alt,
    borderWidth: 1,
    borderColor: appColors.accent.subtleBorder,
    borderRadius: 12,
    paddingVertical: 11,
    paddingHorizontal: 14,
    gap: 12,
  },
  leaderboardChangeRowLeader: {
    backgroundColor: appColors.accent.goldSoft,
    borderColor: appColors.accent.goldBorder,
  },
  leaderboardChangeRankPill: {
    width: 30,
    height: 30,
    borderRadius: 9,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: appColors.surface.elevated,
  },
  leaderboardChangeRankPillLeader: {
    backgroundColor: appColors.accent.gold,
  },
  leaderboardChangeRankPillText: {
    color: appColors.text.secondary,
    fontSize: 14,
    fontWeight: '800',
  },
  leaderboardChangeRankPillTextLeader: {
    color: appColors.background.app,
  },
  leaderboardChangeArrow: {
    fontSize: 16,
    fontWeight: '800',
  },
  leaderboardChangeArrowUp: {
    color: appColors.accent.primary,
  },
  leaderboardChangeArrowDown: {
    color: appColors.status.errorTextMuted,
  },
  leaderboardChangeArrowSame: {
    color: appColors.text.secondary,
  },
  leaderboardChangeName: {
    color: appColors.text.heading,
    flex: 1,
    fontWeight: '700',
    fontSize: 15,
  },
  leaderboardChangeValue: {
    color: appColors.text.secondary,
    fontWeight: '800',
    minWidth: 56,
    textAlign: 'right',
    letterSpacing: 0.4,
  },
  leaderboardChangeValueLeader: {
    color: appColors.accent.gold,
  },
  // Per-game lines under the round positions: the same row, pill and name shapes as above at a
  // smaller size, so one card can hold several games without becoming a wall of full-width rows.
  leaderboardChangeGames: {
    gap: 12,
    marginTop: 2,
  },
  leaderboardChangeGameBlock: {
    gap: 6,
  },
  leaderboardChangeGameName: {
    color: appColors.text.secondary,
    fontSize: 13,
    fontWeight: '800',
    letterSpacing: 0.4,
  },
  leaderboardChangeGameChips: {
    gap: 6,
  },
  leaderboardChangeChip: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: appColors.surface.alt,
    borderWidth: 1,
    borderColor: appColors.accent.subtleBorder,
    borderRadius: 10,
    paddingVertical: 7,
    paddingHorizontal: 10,
    gap: 10,
  },
  leaderboardChangeChipLeader: {
    backgroundColor: appColors.accent.goldSoft,
    borderColor: appColors.accent.goldBorder,
  },
  leaderboardChangeChipPill: {
    width: 22,
    height: 22,
    borderRadius: 7,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: appColors.surface.elevated,
  },
  leaderboardChangeChipPillLeader: {
    backgroundColor: appColors.accent.gold,
  },
  leaderboardChangeChipPillText: {
    color: appColors.text.secondary,
    fontSize: 12,
    fontWeight: '800',
  },
  leaderboardChangeChipPillTextLeader: {
    color: appColors.background.app,
  },
  leaderboardChangeChipName: {
    color: appColors.text.heading,
    flex: 1,
    fontWeight: '700',
    fontSize: 14,
  },
  leaderboardChangeChipScore: {
    color: appColors.text.secondary,
    fontWeight: '800',
    fontSize: 14,
    minWidth: 40,
    textAlign: 'right',
    letterSpacing: 0.4,
  },
  leaderboardChangeChipScoreLeader: {
    color: appColors.accent.gold,
  },
  leaderboardChangeActions: {
    marginTop: 10,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
  },
  leaderboardChangeButtonFlex: {
    flex: 1,
  },
  leaderboardChangeSecondaryButton: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.accent.subtleBorder,
    backgroundColor: appColors.surface.alt,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  leaderboardChangePrimaryButton: {
    borderRadius: 12,
  },
  holeMenuPopupLayer: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 60,
    elevation: 12,
  },
  holeMenuPopupBackdrop: {
    flex: 1,
    backgroundColor: alpha(appColors.surface.tintDeep, 0.1),
  },
  holeMenuPopupCard: {
    position: 'absolute',
    top: 52,
    left: 12,
    maxHeight: 560,
    width: 360,
    backgroundColor: appColors.background.panel,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    paddingVertical: 8,
    ...blockShadow,
  },
  holeMenuScroll: {
    maxHeight: 500,
  },
  holeMenuScrollContent: {
    paddingTop: 4,
    paddingBottom: 4,
  },
  holeMenuRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderTopWidth: 1,
    borderTopColor: appColors.border.strong,
    backgroundColor: 'transparent',
  },
  holeMenuRowCurrent: {
    backgroundColor: alpha(appColors.status.successStrong, 0.2),
  },
  holeMenuRowLeft: {
    flex: 1,
    gap: 2,
    backgroundColor: 'transparent',
  },
  holeMenuRowRight: {
    alignItems: 'flex-end',
    gap: 2,
    marginLeft: 12,
    backgroundColor: 'transparent',
  },
  holeMenuRowTitle: {
    color: appColors.text.primary,
    fontSize: 14,
    fontWeight: '700',
  },
  holeMenuCurrent: {
    color: appColors.accent.primary,
    fontSize: 11,
    fontWeight: '700',
  },
  holeMenuStatus: {
    color: appColors.text.muted,
    fontSize: 12,
  },
  holeMenuScore: {
    color: appColors.text.primary,
    fontSize: 12,
    fontWeight: '700',
  },
  gameResultsHeader: {
    borderBottomWidth: 1,
    borderBottomColor: appColors.border.strong,
    paddingBottom: 2,
    marginBottom: 2,
    backgroundColor: 'transparent',
  },
  matchStatusBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.border.default,
    paddingVertical: 6,
    paddingHorizontal: 8,
    backgroundColor: 'transparent',
  },
  matchStatusIcon: {
    fontSize: 14,
  },
  matchStatusText: {
    flex: 1,
    fontSize: 12,
    fontWeight: '700',
  },
  gameResultsName: {
    flex: 1.4,
    fontWeight: '700',
    fontSize: 12,
    color: appColors.text.primary,
  },
  gameResultsPlayer: {
    flex: 1,
    textAlign: 'center',
    color: appColors.text.primary,
    fontWeight: '600',
    fontSize: 12,
    backgroundColor: 'transparent',
  },
  gameResultCell: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 2,
    paddingHorizontal: 1,
    backgroundColor: 'transparent',
  },
  gameResultValueWrapper: {
    paddingVertical: 2,
    paddingHorizontal: 6,
    backgroundColor: 'transparent',
  },
  gameResultValueContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    backgroundColor: 'transparent',
  },
  gameResultValue: {
    textAlign: 'center',
    fontWeight: '800',
    color: appColors.accent.primary,
  },
  gameResultMedalText: {
    fontSize: 13,
    lineHeight: 16,
  },
  gameResultFishText: {
    fontSize: 12,
    lineHeight: 16,
  },
  gameResultLeaderShared: {
    borderColor: appColors.accent.teal,
  },
  scoreboardNameContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: 'transparent',
  },
  hazardCritter: {
    width: 20,
    height: 20,
  },
  // Same fixed-slot rule as the hazards: reserved whether or not this row has a picture.
  scoreboardAvatarSlot: {
    width: 22,
    height: 22,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
  },
  // Fixed width whether or not it holds anything, so the names below it all start at the same x.
  scoreboardHazardSlot: {
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
  },
  leaderboardToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 10,
    marginTop: 2,
  },
  leaderboardToggleText: {
    color: appColors.text.faint,
    fontSize: 13,
    fontWeight: '600',
  },
  ballBadge: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: appColors.text.subtle,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 6,
  },
  ballHeading: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 4,
    paddingTop: 12,
    paddingBottom: 4,
  },
  ballHeadingText: {
    color: appColors.text.faint,
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 0.6,
  },
  ballBadgeText: {
    color: appColors.background.elevated,
    fontSize: 11,
    fontWeight: '700',
  },
  navRowBottom: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
    marginTop: 12,
    backgroundColor: 'transparent',
  },
  compactGameResultsUnderNav: {
    marginTop: 12,
    backgroundColor: 'transparent',
  },
  primaryButton: {
    marginTop: 8,
    backgroundColor: appColors.background.interactive,
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.accent.mintDeep,
  },
  primaryButtonText: {
    color: appColors.text.primary,
    fontWeight: '800',
    fontSize: 16,
    textAlign: 'center',
  },
  primaryButtonImageMode: {
    width: 220,
    height: 60,
  },
  primaryButtonImageText: {
    fontSize: 16,
  },
  compactNavButtonBlue: {
    width: 168,
    height: 52,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.default,
    backgroundColor: appColors.surface.alt,
  },
  compactNavBlueButtonText: {
    color: appColors.text.secondary,
    fontWeight: '700',
    fontSize: 12,
    letterSpacing: 1.8,
    textTransform: 'uppercase',
    textAlign: 'center',
  },
  compactNavButtonPrimary: {
    borderColor: appColors.accent.primary,
    backgroundColor: appColors.accent.primary,
  },
  compactNavButtonPrimaryText: {
    color: appColors.background.app,
  },
  birdieOverlay: {
    ...StyleSheet.absoluteFillObject,
    // react-native-web honours 'fixed' but the React Native types do not list it. Without the
    // cast this one property fails StyleSheet.create's overload and widens every style in this
    // sheet to TextStyle | ViewStyle | ImageStyle, which is what made <Image style={styles.x}>
    // fail to type-check across the hole screen.
    position: (Platform.OS === 'web' ? 'fixed' : 'absolute') as 'absolute',
    backgroundColor: 'transparent',
    zIndex: 9999,
    elevation: 9999,
    alignItems: 'center',
    justifyContent: 'center',
  },
  birdieImage: {
    width: 300,
    height: 300,
  },
});
